import type { ValidationErrors } from '../../../../../utils/validation';
import { Dropdown, InputRadioGroup } from '../../../../elements/common';
import { FormLabel } from '../../../../elements/forms/FormLabel';
import { HorizontalFormGroup } from '../../../../elements/forms/HorizontalFormGroup';
import { InputContainer } from '../../../../elements/forms/InputContainer';

export interface ICustomControlData {
    diagnostische_overweging: string | null;
    ernst_obstructie: string | null;
    obstructief: string | null;
    restrictief: string | null;
    reversible: string | null;
}

interface ITpoV1DiagnosticReportAddEditCustomControlFieldsProps {
    customControlData: ICustomControlData;
    errors: ValidationErrors;
    handleChangeCustomControl: (values: Partial<ICustomControlData>) => Promise<void>;
    validateOneCustomControl: (name: string) => Promise<void>;
}

export function TpoV1DiagnosticReportAddEditCustomControlFields(
    props: ITpoV1DiagnosticReportAddEditCustomControlFieldsProps
) {
    const handleChangeCustomControl = async (value: Partial<ICustomControlData>) => {
        await props.handleChangeCustomControl(value);
    };

    const validateOneCustomControl = async (name: keyof ICustomControlData) => {
        await props.validateOneCustomControl(name);
    };

    return (
        <>
            <HorizontalFormGroup hasErrors={props.errors.has('customControlData.restrictief')}>
                <FormLabel isRequired>Restrictief</FormLabel>
                <InputContainer>
                    <InputRadioGroup
                        automation="restrictive"
                        error={props.errors.get('customControlData.restrictief')}
                        name="restrictief"
                        onChange={async (value) => await handleChangeCustomControl({ restrictief: value })}
                        onDoValidate={async () => await validateOneCustomControl('restrictief')}
                        options={['Mogelijk', 'Nee']}
                        value={props.customControlData.restrictief}
                    />
                </InputContainer>
            </HorizontalFormGroup>

            <HorizontalFormGroup hasErrors={props.errors.has('customControlData.obstructief')}>
                <FormLabel isRequired>Obstructief</FormLabel>
                <InputContainer>
                    <InputRadioGroup
                        automation="obstructive"
                        error={props.errors.get('customControlData.obstructief')}
                        name="obstructief"
                        onChange={async (value) => {
                            await handleChangeCustomControl({ obstructief: value });
                            if (value === 'Nee') {
                                await handleChangeCustomControl({ ernst_obstructie: null });
                            }
                        }}
                        onDoValidate={async () => await validateOneCustomControl('obstructief')}
                        options={['Ja', 'Nee']}
                        value={props.customControlData.obstructief}
                    />
                </InputContainer>
            </HorizontalFormGroup>

            {props.customControlData.obstructief === 'Ja' && (
                <HorizontalFormGroup hasErrors={props.errors.has('customControlData.ernst_obstructie')}>
                    <FormLabel isRequired>Ernst obstructie</FormLabel>
                    <InputContainer>
                        <InputRadioGroup
                            automation="obstructionDegree"
                            error={props.errors.get('customControlData.ernst_obstructie')}
                            name="ernst_obstructie"
                            onChange={async (value) => await handleChangeCustomControl({ ernst_obstructie: value })}
                            onDoValidate={async () => await validateOneCustomControl('ernst_obstructie')}
                            options={['Licht', 'Matig', 'Ernstig', 'Zeer ernstig']}
                            value={props.customControlData.ernst_obstructie}
                        />
                    </InputContainer>
                </HorizontalFormGroup>
            )}

            <HorizontalFormGroup hasErrors={props.errors.has('customControlData.reversible')}>
                <FormLabel isRequired>Reversibel</FormLabel>
                <InputContainer>
                    <InputRadioGroup
                        automation="reversible"
                        error={props.errors.get('customControlData.reversible')}
                        name="reversible"
                        onChange={async (value) => await handleChangeCustomControl({ reversible: value })}
                        onDoValidate={async () => await validateOneCustomControl('reversible')}
                        options={['Nee', 'Ja', 'Niet bepaald']}
                        value={props.customControlData.reversible}
                    />
                </InputContainer>
            </HorizontalFormGroup>

            <HorizontalFormGroup hasErrors={props.errors.has('customControlData.diagnostische_overweging')}>
                <FormLabel isRequired>Diagnostische overweging</FormLabel>
                <InputContainer>
                    <Dropdown
                        error={props.errors.get('customControlData.diagnostische_overweging')}
                        onChange={async (value) => await handleChangeCustomControl({ diagnostische_overweging: value })}
                        onDoValidate={async () => await validateOneCustomControl('diagnostische_overweging')}
                        selectOptions={diagnosticSelectOptions()}
                        value={props.customControlData.diagnostische_overweging}
                    />
                </InputContainer>
            </HorizontalFormGroup>
        </>
    );
}

function diagnosticSelectOptions() {
    return [
        { value: '', text: 'Kies er een' },
        { value: 'Normaal', text: 'Normaal' },
        { value: 'Mogelijk restrictief', text: 'Mogelijk restrictief' },
        { value: 'Passend bij astma', text: 'Passend bij astma' },
        { value: 'Passend bij dubbeldiagnose astma en COPD', text: 'Passend bij dubbeldiagnose astma en COPD' },
        { value: 'Passend bij COPD: GOLD I', text: 'Passend bij COPD: GOLD I' },
        { value: 'Passend bij COPD: GOLD II', text: 'Passend bij COPD: GOLD II' },
        { value: 'Passend bij COPD: GOLD III', text: 'Passend bij COPD: GOLD III' },
        { value: 'Passend bij COPD: GOLD IV', text: 'Passend bij COPD: GOLD IV' },
        { value: 'Niet te beoordelen', text: 'Niet te beoordelen' },
        { value: 'Anders', text: 'Anders' },
    ];
}

export function tpoV1DiagnosticReportInitializeCustomControlData() {
    return {
        restrictief: null,
        obstructief: null,
        reversible: null,
        ernst_obstructie: null,
        diagnostische_overweging: null,
    };
}
